import { z } from "zod";

// Three-tier Netflix-style reactions per §5.11. Exactly one reaction per
// (userId, target) — upsert on change, delete the row when the user clears.
export const reactionSchema = z.enum(["dislike", "like", "super"]);
export type Reaction = z.infer<typeof reactionSchema>;

export const ratingTargetKindSchema = z.enum(["series", "episode"]);
export type RatingTargetKind = z.infer<typeof ratingTargetKindSchema>;

export const ratingSchema = z.object({
  _id: z.string(),
  userId: z.string(),
  target: z.object({
    kind: ratingTargetKindSchema,
    id: z.string(),
  }),
  reaction: reactionSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Rating = z.infer<typeof ratingSchema>;

// Denormalized aggregate sub-document — lives on each series/episode doc so
// list queries don't need a per-row aggregation. Recomputed on every reaction
// write via $inc, with an hourly reconciliation job repairing drift.
export const ratingAggregateSchema = z.object({
  dislike: z.number().int().nonnegative().default(0),
  like: z.number().int().nonnegative().default(0),
  super: z.number().int().nonnegative().default(0),
  total: z.number().int().nonnegative().default(0),
  // (like + 2*super - dislike) / total — useful for "popular" sort.
  score: z.number().default(0),
});
export type RatingAggregate = z.infer<typeof ratingAggregateSchema>;

// Display rule per §5.11 — translates raw aggregates to a public-facing label.
// Returns null when total < 5 (avoid noisy small-sample displays).
export function ratingDisplay(
  agg: RatingAggregate | undefined,
): { kind: "percent"; value: number } | { kind: "hidden_gem"; superLikes: number } | null {
  if (!agg || agg.total < 5) return null;
  if (agg.total >= 5 && agg.super > 0 && agg.super >= agg.like * 2) {
    return { kind: "hidden_gem", superLikes: agg.super };
  }
  if (agg.total >= 10) {
    const liked = agg.like + agg.super;
    return { kind: "percent", value: Math.round((liked / agg.total) * 100) };
  }
  return null;
}
